// PowerSync integration — offline-first sync to NAS PostgreSQL
//
// Ownership split (per ADR-001):
//   PowerSync owns: offline write queue, local SQLite, retry, transport
//   Bluefly owns: schema definition, conflict policy, business validation
//
// Configuration:
//   VITE_POWERSYNC_URL — PowerSync service endpoint (NAS via Cloudflare Tunnel)
//   VITE_POWERSYNC_TOKEN — JWT from your backend auth service
//
// For MVP without a live PowerSync service, the DB runs in fully local mode.
// All reads/writes go to local SQLite. uploadData() is a no-op until the
// service URL is configured.

import {
  PowerSyncDatabase,
  AbstractPowerSyncDatabase,
  column,
  Schema,
  Table,
} from '@powersync/web';

// ─── Schema — matches the PostgreSQL World Model on the NAS ───────────────────

const trips = new Table({
  name:        column.text,
  region_id:   column.text,
  started_at:  column.text,
  ended_at:    column.text,
  notes:       column.text,
  sync_status: column.text,
  created_at:  column.text,
  updated_at:  column.text,
});

const catches = new Table({
  trip_id:      column.text,
  waypoint_id:  column.text,
  species_name: column.text,
  weight_lbs:   column.real,
  length_in:    column.real,
  photo_ids:    column.text,   // JSON array serialized
  notes:        column.text,
  lat:          column.real,
  lng:          column.real,
  caught_at:    column.text,
  created_at:   column.text,
  updated_at:   column.text,
});

const waypoints = new Table({
  trip_id:    column.text,
  label:      column.text,
  notes:      column.text,
  lat:        column.real,
  lng:        column.real,
  type:       column.text,
  created_at: column.text,
  updated_at: column.text,
});

export const AppSchema = new Schema({ trips, catches, waypoints });
export type AppDatabase = (typeof AppSchema)['types'];

// ─── Singleton DB instance ────────────────────────────────────────────────────

let _db: PowerSyncDatabase | null = null;

export function getPowerSyncDB(): PowerSyncDatabase {
  if (_db) return _db;

  _db = new PowerSyncDatabase({
    schema: AppSchema,
    database: {
      dbFilename: 'bluguide.db',
    },
  });

  return _db;
}

// ─── Connect to live sync service (call when online + authenticated) ──────────

export async function connectSync(db: PowerSyncDatabase): Promise<void> {
  const url = import.meta.env.VITE_POWERSYNC_URL;
  const token = import.meta.env.VITE_POWERSYNC_TOKEN;

  if (!url || !token) {
    console.info('[PowerSync] No service URL configured — running in local-only mode');
    return;
  }

  // PowerSync connector — Bluefly owns the uploadData implementation.
  // This is where conflict policy lives: last-write-wins via updated_at.
  await db.connect({
    fetchCredentials: async () => ({
      endpoint: url,
      token,
    }),
    uploadData: async (database: AbstractPowerSyncDatabase) => {
      const batch = await database.getCrudBatch(200);
      if (!batch) return;

      try {
        // POST the batch to our NAS backend which applies LWW conflict policy
        const res = await fetch(`${url}/api/sync/upload`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ops: batch.crud }),
        });

        if (!res.ok) {
          throw new Error(`Upload failed: ${res.status}`);
        }

        await batch.complete();
      } catch (err) {
        console.error('[PowerSync] uploadData error — will retry:', err);
        // PowerSync retries automatically; do not call batch.complete()
      }
    },
  });

  console.info('[PowerSync] Connected to', url);
}

// ─── Sync status helpers ──────────────────────────────────────────────────────

export async function getSyncStatus(db: PowerSyncDatabase) {
  const status = db.currentStatus;
  return {
    connected: status.connected,
    lastSyncedAt: status.lastSyncedAt,
    hasSynced: status.hasSynced,
    dataFlowStatus: status.dataFlowStatus,
  };
}
